import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import {
  createJobRequestSchema,
  JOB_REQUEST_MAX,
  JOB_TASK_COUNT_MAX,
  Language,
  LANGUAGES,
} from '@quizdb/common';
import { problemCode } from '../../api/problemCode';
import { Icon } from '../../common/Icon';
import { useListSourcesQuery } from '../../source/sourceEndpoints';
import { useCreateJobMutation } from '../jobEndpoints';

/** The shape a zod issue needs for `mapIssues`; `ZodIssue` satisfies this. */
interface Issue {
  path: readonly PropertyKey[];
  message: string;
}

/** `createJobRequestSchema`'s top-level keys, mapped to the form's field ids (QZ-D48). */
const FIELD_IDS: Record<string, string> = {
  language: 'job-language',
  taskCount: 'job-task-count',
  request: 'job-request',
};

/** Maps the schema's issues to the form's field ids, keyed by `issue.path[0]` (QZ-D48). */
const mapIssues = (issues: readonly Issue[]): Record<string, string[]> => {
  const fields: Record<string, string[]> = {};
  for (const issue of issues) {
    const id = FIELD_IDS[String(issue.path[0])];
    if (!id) {
      continue;
    }
    const list = fields[id] ?? [];
    if (!list.includes(issue.message)) {
      fields[id] = [...list, issue.message];
    }
  }
  return fields;
};

/** A field's issues, shown under it, as `TaskForm`'s `FieldError` does. */
const FieldError = ({ id, messages }: { id: string; messages?: string[] }) => {
  if (!messages || messages.length === 0) {
    return null;
  }
  return (
    <div id={`${id}-error`} className="invalid-feedback d-block">
      {messages.map((message, index) => (
        <div key={index}>{message}</div>
      ))}
    </div>
  );
};

/**
 * The `/jobs/new` form for a prompt job (QZ-D48): a job language (QZ-D22, default `sv`), a
 * task count and a request. Follows `job-new.html`'s *Prompt* state; the method chooser and
 * the PDF and photo states come in later milestones.
 */
export const NewJobPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: sourcesData } = useListSourcesQuery();
  const [createJob, { isLoading }] = useCreateJobMutation();

  const [language, setLanguage] = useState<Language>('sv');
  const [taskCount, setTaskCount] = useState('40');
  const [request, setRequest] = useState('');
  const [fields, setFields] = useState<Record<string, string[]>>({});
  const [submitError, setSubmitError] = useState<string | undefined>();

  const sourcesLoaded = sourcesData !== undefined;
  const sources = sourcesData?.sources ?? [];
  const noSources = sourcesLoaded && sources.length === 0;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFields({});
    setSubmitError(undefined);

    const parsed = createJobRequestSchema.safeParse({
      request: request.trim(),
      language,
      taskCount: Number(taskCount),
    });

    if (!parsed.success) {
      setFields(mapIssues(parsed.error.issues));
      return;
    }

    try {
      const job = await createJob(parsed.data).unwrap();
      navigate(`/jobs/${job.jobId}`);
    } catch (thrown) {
      const code = problemCode(thrown);
      setSubmitError(t(code ? `error.${code}` : 'error.unknown'));
    }
  };

  return (
    <main className="qz-main">
      <div className="qz-container is-narrow">
        <Link className="qz-back" to="/jobs">
          <Icon name="arrow-left" /> {t('job.page.back')}
        </Link>

        <div className="qz-page-header">
          <div>
            <h1>{t('job.new.heading')}</h1>
            <p className="qz-sub">{t('job.new.sub')}</p>
          </div>
        </div>

        {/* noValidate: the min/max on task count are a UX hint, not a gate — zod owns validation. */}
        <form className="qz-card" onSubmit={handleSubmit} noValidate>
          <div className="qz-card-body qz-stack">
            <div className="row g-3">
              <div className="col-sm-6">
                <label htmlFor="job-language" className="form-label">
                  {t('job.field.language')}
                </label>
                <select
                  id="job-language"
                  className={`form-select${fields['job-language'] ? ' is-invalid' : ''}`}
                  aria-describedby={fields['job-language'] ? 'job-language-error' : undefined}
                  value={language}
                  onChange={(e) => setLanguage(e.target.value as Language)}
                >
                  {LANGUAGES.map((code) => (
                    <option key={code} value={code}>
                      {t(`task.language.${code}`)}
                    </option>
                  ))}
                </select>
                <div className="form-text">{t('job.new.languageHelp')}</div>
                <FieldError id="job-language" messages={fields['job-language']} />
              </div>
              <div className="col-sm-6">
                <label htmlFor="job-task-count" className="form-label">
                  {t('job.field.taskCount')}
                </label>
                <input
                  id="job-task-count"
                  type="number"
                  min={1}
                  max={JOB_TASK_COUNT_MAX}
                  step={1}
                  className={`form-control${fields['job-task-count'] ? ' is-invalid' : ''}`}
                  aria-describedby={fields['job-task-count'] ? 'job-task-count-error' : undefined}
                  value={taskCount}
                  onChange={(e) => setTaskCount(e.target.value)}
                />
                <div className="form-text">{t('job.new.taskCountHelp')}</div>
                <FieldError id="job-task-count" messages={fields['job-task-count']} />
              </div>
            </div>

            <div>
              <label htmlFor="job-request" className="form-label">
                {t('job.field.request')}
              </label>
              <textarea
                id="job-request"
                rows={4}
                maxLength={JOB_REQUEST_MAX}
                className={`form-control${fields['job-request'] ? ' is-invalid' : ''}`}
                aria-describedby={fields['job-request'] ? 'job-request-error' : undefined}
                value={request}
                onChange={(e) => setRequest(e.target.value)}
              />
              <div className="form-text">{t('job.new.requestHelp')}</div>
              <FieldError id="job-request" messages={fields['job-request']} />

              <div className="qz-small d-flex align-items-center gap-2 flex-wrap mt-2">
                {noSources ? (
                  <span className="text-danger">{t('error.NoSources')}</span>
                ) : (
                  <>
                    <span className="qz-muted">{t('job.new.researchesOn')}</span>
                    {sourcesLoaded &&
                      sources.map((source) => (
                        <span key={source.domain} className="qz-tag">
                          {source.domain}
                        </span>
                      ))}
                  </>
                )}
                <Link to="/sources">{t('job.new.editSources')}</Link>
              </div>
            </div>

            {submitError && (
              <div className="alert alert-danger">
                <Icon name="circle-exclamation" />
                <div>{submitError}</div>
              </div>
            )}
          </div>

          <div className="qz-card-footer d-flex align-items-center gap-2">
            <span className="qz-small qz-muted">
              <Icon name="circle-info" /> {t('job.new.costNote')}
            </span>
            <Link to="/jobs" className="btn btn-outline-secondary ms-auto">
              {t('job.new.cancel')}
            </Link>
            <button type="submit" className="btn btn-primary" disabled={isLoading || noSources}>
              <Icon name="wand-magic-sparkles" /> {t('job.new.start')}
            </button>
          </div>
        </form>
      </div>
    </main>
  );
};

export default NewJobPage;
